import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert, Linking, PermissionsAndroid, Platform } from 'react-native';
import {
  launchCamera,
  launchImageLibrary,
  type CameraOptions,
  type ImageLibraryOptions,
  type ImagePickerResponse,
} from 'react-native-image-picker';
import { translateCurrent } from '../i18n';

type TonytePermission = 'location' | 'notifications' | 'camera' | 'microphone';

type PermissionResult = 'granted' | 'denied' | 'blocked' | 'unavailable';
type AndroidPermission = Parameters<typeof PermissionsAndroid.check>[0];

const CONTEXT_SEEN_PREFIX = '@tonyte/permission-context/';
const ANDROID_13 = 33;

const CONTEXT_COPY: Record<TonytePermission, { titleKey: string; messageKey: string }> = {
  location: {
    titleKey: 'permissions.locationTitle',
    messageKey: 'permissions.locationMessage',
  },
  notifications: {
    titleKey: 'permissions.notificationsTitle',
    messageKey: 'permissions.notificationsMessage',
  },
  camera: {
    titleKey: 'permissions.cameraTitle',
    messageKey: 'permissions.cameraMessage',
  },
  microphone: {
    titleKey: 'permissions.microphoneTitle',
    messageKey: 'permissions.microphoneMessage',
  },
};

const showContextOnce = async (permission: TonytePermission) => {
  const key = `${CONTEXT_SEEN_PREFIX}${permission}`;
  const seen = await AsyncStorage.getItem(key);
  if (seen === 'true') return;

  const copy = CONTEXT_COPY[permission];
  await new Promise<void>(resolve => {
    Alert.alert(
      translateCurrent(copy.titleKey),
      translateCurrent(copy.messageKey),
      [{ text: translateCurrent('common.continue'), onPress: () => resolve() }],
      {
      cancelable: true,
      onDismiss: () => resolve(),
      },
    );
  });
  await AsyncStorage.setItem(key, 'true');
};

const showDeniedAlert = (permission: TonytePermission, blocked = false) => {
  const label = translateCurrent(`permissions.${permission}Label`, permission);
  Alert.alert(
    blocked ? translateCurrent('permissions.disabled') : translateCurrent('permissions.notGranted'),
    blocked
      ? translateCurrent('permissions.blockedBody').replace('{permission}', label)
      : translateCurrent('permissions.deniedBody').replace('{permission}', label),
    blocked
      ? [
          { text: translateCurrent('common.notNow'), style: 'cancel' },
          { text: translateCurrent('common.openSettings'), onPress: () => Linking.openSettings().catch(() => undefined) },
        ]
      : [{ text: translateCurrent('common.ok') }],
  );
};

const requestAndroidPermission = async (
  permission: AndroidPermission,
  feature: TonytePermission,
): Promise<PermissionResult> => {
  const current = await PermissionsAndroid.check(permission);
  console.log('[Tonyte][RN][Permissions]', feature, 'current state:', current ? 'granted' : 'not granted');
  if (current) return 'granted';

  await showContextOnce(feature);
  const result = await PermissionsAndroid.request(permission);
  console.log('[Tonyte][RN][Permissions]', feature, 'request result:', result);
  if (result === PermissionsAndroid.RESULTS.GRANTED) return 'granted';
  if (result === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) {
    showDeniedAlert(feature, true);
    return 'blocked';
  }
  showDeniedAlert(feature);
  return 'denied';
};

export async function requestLocationPermission(): Promise<PermissionResult> {
  if (Platform.OS !== 'android') {
    await showContextOnce('location');
    return 'unavailable';
  }

  await showContextOnce('location');
  const fine = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION);
  if (fine === PermissionsAndroid.RESULTS.GRANTED) return 'granted';

  const coarse = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.ACCESS_COARSE_LOCATION);
  if (coarse === PermissionsAndroid.RESULTS.GRANTED) return 'granted';

  const blocked = fine === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN || coarse === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN;
  showDeniedAlert('location', blocked);
  return blocked ? 'blocked' : 'denied';
}

export async function requestNotificationPermission(): Promise<PermissionResult> {
  if (Platform.OS === 'android') {
    if (Number(Platform.Version) < ANDROID_13) return 'granted';
    return requestAndroidPermission(PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS, 'notifications');
  }

  await showContextOnce('notifications');
  return 'unavailable';
}

export async function requestCameraPermission(): Promise<PermissionResult> {
  if (Platform.OS !== 'android') {
    await showContextOnce('camera');
    return 'unavailable';
  }
  return requestAndroidPermission(PermissionsAndroid.PERMISSIONS.CAMERA, 'camera');
}

// Dev-only diagnostic for the microphone permission flow specifically —
// the other permission flows share requestAndroidPermission's generic log,
// but this one got its own dedicated state machine (see
// requestMicrophonePermission below) after a release-build bug where voice
// recording looped on a permission error despite RECORD_AUDIO genuinely
// being granted (root cause: missing ProGuard keep rules for the Nitro
// native module, fixed in android/app/proguard-rules.pro — this logging
// stays as a fast way to confirm the JS-side permission state itself is
// correct if a similar-looking bug ever resurfaces). Never runs in
// production — __DEV__ is compiled out of release JS bundles entirely.
const logMicrophoneDiagnostic = (stage: string, details: Record<string, unknown>) => {
  if (!__DEV__) return;
  console.log('[Tonyte][RN][Permissions][mic]', stage, {
    platform: Platform.OS,
    androidApiLevel: Platform.OS === 'android' ? Platform.Version : undefined,
    ...details,
  });
};

export async function requestMicrophonePermission(): Promise<PermissionResult> {
  if (Platform.OS !== 'android') {
    // iOS: the native recorder itself calls AVAudioSession.requestRecordPermission
    // when startRecorder() is invoked, which shows the real system prompt (once)
    // and rejects with a permission-flavored error if denied/blocked — so there's
    // nothing to gate here; deferring to that keeps this in sync with the actual
    // OS-level state instead of duplicating/guessing it.
    logMicrophoneDiagnostic('ios-defer-to-native-prompt', {});
    await showContextOnce('microphone');
    return 'granted';
  }

  const permission = PermissionsAndroid.PERMISSIONS.RECORD_AUDIO;
  const alreadyGranted = await PermissionsAndroid.check(permission);
  logMicrophoneDiagnostic('initial-check', { rawResult: alreadyGranted, normalizedState: alreadyGranted ? 'granted' : 'not-granted' });
  if (alreadyGranted) return 'granted';

  await showContextOnce('microphone');
  const requestResult = await PermissionsAndroid.request(permission);
  logMicrophoneDiagnostic('request-result', { rawResult: requestResult });

  if (requestResult === PermissionsAndroid.RESULTS.GRANTED) {
    logMicrophoneDiagnostic('normalized-state', { normalizedState: 'granted' });
    return 'granted';
  }
  if (requestResult === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) {
    logMicrophoneDiagnostic('normalized-state', { normalizedState: 'blocked' });
    showDeniedAlert('microphone', true);
    return 'blocked';
  }
  logMicrophoneDiagnostic('normalized-state', { normalizedState: 'denied' });
  showDeniedAlert('microphone');
  return 'denied';
}

// Read-only status probe — no request dialog, no alert. Used to silently
// re-sync UI state when the app resumes from the background (e.g. the user
// went to Settings and enabled the microphone, then switched back to
// Tonyte) without waiting for the user to tap the mic button again.
export async function checkMicrophonePermissionSilently(): Promise<PermissionResult> {
  if (Platform.OS !== 'android') return 'granted';
  const granted = await PermissionsAndroid.check(PermissionsAndroid.PERMISSIONS.RECORD_AUDIO);
  logMicrophoneDiagnostic('appstate-resume-check', { rawResult: granted, normalizedState: granted ? 'granted' : 'not-granted' });
  return granted ? 'granted' : 'denied';
}

const handlePickerPermissionError = (response: ImagePickerResponse, permission: TonytePermission) => {
  if (response.errorCode === 'permission') {
    showDeniedAlert(permission, true);
  }
};

export async function pickImageFromLibrary(options: ImageLibraryOptions): Promise<ImagePickerResponse | null> {
  const response = await launchImageLibrary(options);
  console.log('[Tonyte][ImagePreview] image library response', {
    didCancel: response.didCancel === true,
    errorCode: response.errorCode,
    assetCount: response.assets?.length ?? 0,
    uri: response.assets?.[0]?.uri ?? null,
  });
  return response;
}

export async function takePhotoWithCamera(options: CameraOptions): Promise<ImagePickerResponse | null> {
  const permission = await requestCameraPermission();
  if (Platform.OS === 'android' && permission !== 'granted') return null;

  const response = await launchCamera(options);
  console.log('[Tonyte][ImagePreview] camera response', {
    didCancel: response.didCancel === true,
    errorCode: response.errorCode,
    assetCount: response.assets?.length ?? 0,
    uri: response.assets?.[0]?.uri ?? null,
  });
  handlePickerPermissionError(response, 'camera');
  return response;
}
